import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { PlayList } from "../Components/Sounds/PlayList";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { BetManager } from "../Managers/BetManager";
import { Payline } from "../GameComponents/Payline";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { ArrayUtils } from "neo-common/Engine/Utils/ArrayUtils";
import { Tweens, TweensAttributes } from "../Components/Effects/Tweens";
import { Attributes } from "../Utils/Attributes";
import { ReelExtended } from "./ReelExtended";
import { ModuleManager } from "../Managers/ModuleManager";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { AppContext } from "../../Common/AppContext";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Languages } from "../Utils/Languages";
import { SwitchStyleCaseAttributes } from "../Components/Elements/Styles/SwitchStyle";
import { BaseView } from "../GameComponents/Base/BaseView";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    public static Force: boolean = false;

    //******************************************************************************************************************
    //  CONFIG
    //******************************************************************************************************************

    public static ExpandDuration: number = 105; //45f - ExpandAnimation 60f - SymbolTextShine text anim

    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static ExpandWildAnimFinish: string = "eventExpandWildAnimFinish";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public _expandWildsDelay: FrameTimerEventEmitter;

    public _canPlay: boolean = true;

    public _expandWildsContainer: ContainerElement;

    public _expandWildsRotation: ContainerElement;

    public _expandWilds: Array<SwitchElement>;

    public currentWinSymbolsContainer: ContainerElement;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._expandWildsDelay = CreateFrameTimer("expandWildsDelay");
        this._expandWildsDelay.on(FrameTimerEvents.Complete, this.expandWildFinished.bind(this));
    }

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    public initExpandWilds(expandWildsContainer: ContainerElement, expandRotation: ContainerElement): void {
        this._expandWilds = [];
        this._expandWildsContainer = expandWildsContainer;
        this._expandWildsRotation = expandRotation;

        if (expandWildsContainer) {
            for (let i = 0; i < this.reelCount; i++) {
                this._expandWilds.push(expandWildsContainer.childs["ExpandWild" + i]);
            }
        }
    }

    public onReelStopped(
        reelsData: Array<Communication.Game.IReel>,
        reelIndex: number,
        reelsTransition?: SwitchElement,
    ): void {
        super.onReelStopped(reelsData, reelIndex);

        if (!WinAnimatorExtended.Force) {
            this.impactSpecialSymbols(reelsData, reelIndex);
        }

        if (!ReelExtended.Tease) {
            this.impactBonus(reelsData, reelIndex);
            this.impactWilds(reelsData, reelIndex);
        }

        this.moveToTop();
    }

    private impactSpecialSymbols(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (
            (reelIndex < 3 || ReelExtended.Tease) &&
            reelsData[reelIndex].symbols.indexOf(this.getTeasingSymbol()) !== -1
        ) {
            PlayList.play("SND_BaseBonusHit" + reelIndex);
        }

        this.tease(reelIndex);
    }

    protected tease(reelIndex: number): void {
        const count = this.countScatters(reelIndex);

        if (count >= this.getMinTeasingSymbolCount()) {
            this.tease3(reelIndex, count);
        } else if (count === this.getMinTeasingSymbolCount() - 1) {
            this.tease2(reelIndex, count);
        } else {
            this.tease1(reelIndex, count);
        }
    }

    protected countScatters(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            if (this.currentReels[i].symbols.indexOf(this.getTeasingSymbol()) !== -1) {
                count++;
            }
        }

        return count;
    }

    protected tease1(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            return;
        }

        const symbolIndex = this.currentReels[reelIndex].symbols.indexOf(this.getTeasingSymbol());

        if (symbolIndex !== -1) {
            if (this.allowSymbolGoToCase(reelIndex, symbolIndex, Symbol.CaseImpactWithTease)) {
                this.currentSymbols[reelIndex][symbolIndex].goToCase(Symbol.CaseImpactWithTease);
                // PlayList.play('SND_BaseScatter');
            }
        }
    }

    protected tease2(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            for (let i = 0; i < reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactWithTease)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseImpactWithTease);
                    }
                }
            }

            return;
        }

        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j].id === this.getTeasingSymbol() - 1) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseTease)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseTease);
                    }
                } else {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }

        this.tease3(reelIndex, count);
    }

    protected tease3(reelIndex: number, count: number): void {
        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            if (this.currentSymbols[reelIndex][i].id === this.getTeasingSymbol() - 1) {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseImpactWithTease)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpactWithTease);
                }
            } else {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseNowin)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseNowin);
                }
            }
        }
    }

    protected getTeasingSymbol(): number {
        return 11;
    }

    protected getMinTeasingSymbolCount(): number {
        return 3;
    }

    protected impactBonus(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
                if (reelsData[reelIndex].symbols[i] === 11 && this.currentSymbols[reelIndex][i] != null) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpact);
                    PlayList.play("SND_BaseBonusHit" + reelIndex);
                }
            }
        }
    }

    protected impactWilds(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
                if (reelsData[reelIndex].symbols[i] === 12 && this.currentSymbols[reelIndex][i] != null) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpact);
                    PlayList.play("SND_FSWildHitFS" + reelIndex);
                }
            }
        }
    }

    private calculatePreviousStars(
        reelsData: Array<Communication.Game.IReel>,
        reelIndex: number,
        symbolId: number,
    ): number {
        let count: number = 0;
        for (let j: number = 0; j < reelIndex; j++) {
            if (reelsData[j] != null && reelsData[j].symbols != null) {
                for (let i: number = 0; i < reelsData[j].symbols.length; i++) {
                    if (reelsData[j].symbols[i] === symbolId) {
                        count++;
                    }
                }
            }
        }

        return count;
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        if (this.currentWins && this.currentWins.length > 0) {
            if (this.currentWins.length > 1) {
                //if scatter win, we get 2 wins (cash, fs) - still single win
                if (
                    this.currentWins[0].trigger == Communication.Game.WinTrigger.Scatter &&
                    this.currentWins.length == 2
                ) {
                    this.winSymbolsGoToCase(0, Symbol.CaseWin, true);
                    PlayList.play("SND_BaseRecapWinline");

                    if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                        PlayList.loop("SND_BaseRecapLoop");
                    }

                    return;
                }

                this.stopTimer();
                this.currentCycle = 0;
                this.cycleWins(true);
            } else {
                this.winSymbolsGoToCase(0, Symbol.CaseWin, true);
                PlayList.play("SND_BaseRecapWinline");

                if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                    PlayList.loop("SND_BaseRecapLoop");
                }
            }
        }
    }

    public showInitWinsBonus(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();
        if (wins != null && wins.length > 0 && this.currentWins[0].trigger != Communication.Game.WinTrigger.Scatter) {
            this.showCustomInitWin();
            this.showFirstInitWins();
            this.finishFirstInitWins();
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
        this.moveToTop();
    }

    protected startAnimation(): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.currentWin);
        }

        this.playGenieWinSound();
    }

    protected playGenieWinSound(): void {
        let hasGenieWin = false;
        for (let winIndex = 0; winIndex < this.currentWins.length; winIndex++) {
            const win = this.currentWins[winIndex];
            if (win.payoutId == 13) {
                hasGenieWin = true;
            }
        }

        if (hasGenieWin) {
            PlayList.play("SND_BaseBonusHaha");
        }
    }

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();
        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();
            this.finishFirstInitWins();
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
        this.moveToTop();
    }

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }
            this.moveToTop();
            return this.currentMultiplierIndex >= 3;
        } else {
            this.allSymbolsGoToCase(Symbol.CaseNormal, false);
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            this.moveToTop();
            return false;
        }
    }

    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger == Communication.Game.WinTrigger.Scatter) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin + "_END", false);
            } else {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            }

            this.winPaylineGoToCase(i, Payline.CaseFirstWin);
        }
    }

    protected finishFirstInitWins(): void {
        if (this.currentWins.length > 0) {
            if (this.currentWins.length > 1) {
                //if scatter win, we get 2 wins (cash, fs) - still single win
                if (
                    this.currentWins[0].trigger == Communication.Game.WinTrigger.Scatter &&
                    this.currentWins.length == 2
                ) {
                    this.winSymbolsGoToCase(0, Symbol.CaseWin, true);
                    PlayList.play("SND_BaseRecapWinline");

                    if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                        PlayList.loop("SND_BaseRecapLoop");
                    }

                    return;
                }

                this.stopTimer();
                this.currentCycle = 0;
                this.cycleWins(true);
            } else {
                this.winSymbolsGoToCase(0, Symbol.CaseWin, true);
                PlayList.play("SND_BaseRecapWinline");

                if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                    PlayList.loop("SND_BaseRecapLoop");
                }
            }
        }
    }

    protected cycleWins(isFirst: boolean = false): void {
        if (this.currentWins == null) {
            return;
        }

        //skip freespin cycle win
        if (this.currentWins[this.currentCycle].type === Communication.Game.WinType.freeSpins) {
            this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;
            this.cycleWins(isFirst);

            return;
        }

        for (let i = 0; i < this.currentWins.length; i++) {
            if (i == this.currentCycle) {
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false, false);
            } else {
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false, true);
            }
        }
        super.cycleWins(isFirst);
    }

    protected isScatterWin(currentWin: Communication.Game.IWin): boolean {
        if (
            currentWin.trigger == Communication.Game.WinTrigger.Scatter &&
            currentWin.type == Communication.Game.WinType.freeSpins
        ) {
            return true;
        }

        return false;
    }

    protected hasScatterWin(currentWins: Array<Communication.Game.IWin>): boolean {
        for (let i = 0; i < currentWins.length; i++) {
            if (this.isScatterWin(currentWins[i])) {
                return true;
            }
        }

        return false;
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            if (BaseView.PaylinesIndicator != null) {
                this.addChild(BaseView.PaylinesIndicator);
                BaseView.PaylinesIndicator.goToCase("HIDE");
            }

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    const symbol = this.currentSymbols[i][definition[i]];

                    if (symbol == null) {
                        this._expandWilds[i].goToCase("WIN");
                    } else {
                        if (caseType === Symbol.CaseCycle) {
                            symbol.goToCase(Symbol.CaseCycle + "_" + i);
                        } else {
                            symbol.goToCase(caseType);
                        }

                        if (
                            (caseType == Symbol.CaseWin || caseType == Symbol.CaseCycle) &&
                            this.currentWinSymbolsContainer != null
                        ) {
                            this.currentWinSymbolsContainer.addChild(symbol);
                        } else {
                            this.symbolsContainer.addChild(symbol);
                        }

                        symbols.push(symbol);
                    }
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    if (symbol != null) {
                        if (symbol.getCurrentCase() != Symbol.CaseWin + "_END") {
                            if (caseType === Symbol.CaseCycle) {
                                symbol.goToCase(Symbol.CaseCycle + "_" + i);
                            } else {
                                symbol.goToCase(caseType);
                            }
                        }
                        this.symbolsContainer.addChild(symbol);
                        symbols.push(symbol);
                    }
                }
            }
        }

        return symbols;
    }

    protected getAnimationIndex(): number {
        if (this.currentWins.length === 1 && this.currentWins[0].type === Communication.Game.WinType.freeSpins) {
            return 6;
        } else {
            return super.getAnimationIndex();
        }
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        if (this.currentSymbols[reelIndex][symbolIndex] != null) {
            if (
                (this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle ||
                    this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle + "_2" ||
                    this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle + "_3") &&
                caseType === Symbol.CaseNormal
            ) {
                return false;
            }

            this.moveToTop();

            return true;
        }

        return true;
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.addCurrentSymbol(reelIndex, symbolIndex);

        this.moveToTop();
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean, scaleDown?: boolean): void {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        switch (win.trigger) {
            case Communication.Game.WinTrigger.Payline:
                symbols = this.winSymbolsGoToCaseAsPayline(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Scatter:
                symbols = this.winSymbolsGoToCaseAsScatter(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Way:
                symbols = this.winSymbolsGoToCaseAsWay(winIndex, caseType);
                break;
            default:
                symbols = this.winSymbolsGoToCaseAsCustom(winIndex, caseType);
                break;
        }

        if (
            useValue &&
            (symbols.length > 0 || this._expandWilds[0].getCurrentCase() != Symbol.CaseEmpty) &&
            win.amount > 0
        ) {
            this.value.visible = true;

            if (this._expandWilds[0].getCurrentCase() != Symbol.CaseEmpty) {
                const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                    win.paylineId,
                    this.paylines.paylineDefinition,
                );

                this.value.position.x = this._expandWilds[0].position.x;
                this.value.position.y = definition[0] * this.symbolHeight;
            } else {
                this.value.position.x = symbols[0].position.x + symbols[0].offsetX;
                this.value.position.y = symbols[0].position.y + symbols[0].offsetY;
            }

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.value.play(parseInt(NumberUtils.devide(win.amount, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                this.value.play(parseFloat(Languages.formatText(Languages.FormatMoney, win.amount.toString())));
            }
        } else {
            this.value.visible = false;
        }

        if (scaleDown != null && scaleDown) {
            const win = this.currentWins[winIndex];

            if (this.paylines != null) {
                if (win.trigger == Communication.Game.WinTrigger.Payline) {
                    let definition: Array<number> = PaylinesDefinitions.getPaylineDefinitionsById(
                        win.paylineId,
                        this.paylines.paylineDefinition,
                    );
                    for (let i = 0; i < definition.length; i++) {
                        if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                            let symbol: Symbol = <any>this.currentSymbols[i][definition[i]];
                            if (symbol != null) {
                                this.scaleSymbols(symbol, [1, 0.8], [10], true);
                            }
                        }
                    }
                } else if (win.trigger == Communication.Game.WinTrigger.Scatter) {
                    for (let i = 0; i < this.reelCount; i++) {
                        for (let j = 0; j < this.symbolCount; j++) {
                            if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                                let symbol: Symbol = this.currentSymbols[i][j];

                                if (symbol != null) {
                                    this.scaleSymbols(symbol, [1, 0.8], [10], true);
                                }
                            }
                        }
                    }
                }
            }
        } else {
            const win = this.currentWins[winIndex];

            if (this.paylines != null) {
                if (win.trigger == Communication.Game.WinTrigger.Payline) {
                    const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                        win.paylineId,
                        this.paylines.paylineDefinition,
                    );

                    for (let i = 0; i < definition.length; i++) {
                        if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                            let symbol: Symbol = <any>this.currentSymbols[i][definition[i]];

                            if (symbol != null) {
                                this.scaleSymbols(symbol, [0.8, 1], [10], false);
                            }
                        }
                    }
                } else if (win.trigger == Communication.Game.WinTrigger.Scatter) {
                    for (let i = 0; i < this.reelCount; i++) {
                        for (let j = 0; j < this.symbolCount; j++) {
                            if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                                let symbol: Symbol = this.currentSymbols[i][j];

                                if (symbol != null) {
                                    this.scaleSymbols(symbol, [0.8, 1], [10], false);
                                }
                            }
                        }
                    }
                }
            }
        }

        this.moveToTop();
    }

    protected scaleSymbols(
        symbol: Symbol,
        tweenValuesOrig: Array<number>,
        tweenDuration: Array<number>,
        equal: boolean,
    ) {
        let container: ContainerElement = <any>symbol.children[0];
        Tweens.removeTweens(container);
        let chooseIf: boolean;
        if (equal) {
            chooseIf = container.scale.x == 1;
        } else {
            chooseIf = container.scale.x != 1;
        }

        if (chooseIf) {
            let tween: string = "scale.x";
            let tweenVal: Array<number> = tweenValuesOrig.slice(0);
            let tweenValues: Array<number> = tweenValuesOrig;
            let tweenEase: Array<string> = [];
            let tweenDelay: Array<number> = [0];
            let tweenLoop: boolean = false;

            container.pivot.x = this.symbolWidth / 2;
            container.pivot.y = this.symbolHeight / 2;
            container.position.x = this.symbolWidth / 2;
            container.position.y = this.symbolHeight / 2;
            Tweens.customTween(container, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            tween = "scale.y";
            tweenValues = tweenVal;
            Tweens.customTween(container, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
        }
    }

    public reset(showLastSymbols: boolean): boolean {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j] != null) {
                    let symbol: Symbol = <any>this.currentSymbols[i][j];
                    let container: ContainerElement = <any>symbol.children[0];
                    container.pivot.x = 0;
                    container.pivot.y = 0;
                    container.position.x = 0;
                    container.position.y = 0;
                    container.scale.x = 1;
                    container.scale.y = 1;
                    Tweens.removeTweens(container);
                }
            }
        }

        return super.reset(showLastSymbols);
    }

    protected getIdleDelay(): number {
        return 30;
    }

    protected getInitialIdleDelay(): number {
        return 30;
    }

    protected getIdleActiveElementsCount(): number {
        return 2;
    }

    protected moveToTop(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && (symbol.id === 10 || symbol.id === 11)) {
                    symbol.parent.addChild(symbol);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  EXPAND
    //******************************************************************************************************************

    public showExpandWilds(reelsData: Array<Communication.Game.IReel>, init: boolean = false): void {
        let hasExpandWild = false;

        this.allSymbolsGoToCase(Symbol.CaseNormal, true);

        for (let i = 0; i < reelsData.length; i++) {
            if (reelsData[i].transform) {
                if (init) {
                    this._expandWilds[i].goToCase("INIT");
                } else {
                    this._expandWilds[i].goToCase("START");
                    hasExpandWild = true;
                }

                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];

                    if (init) {
                        symbol.visible = false;
                    } else {
                        Tweens.removeTweens(symbol);
                        Tweens.customTween(symbol, {
                            tween: "alpha",
                            tweenValues: [1, 0],
                            tweenDuration: [WinAnimatorExtended.ExpandDuration * 0.5],
                        });
                    }
                }

                this._expandWildsContainer.addChild(this._expandWilds[i]);
                this._expandWilds[i].position.y = 0;
            } else {
                this._expandWilds[i].goToCase("EMPTY");
            }
        }

        if (!init) {
            if (hasExpandWild) {
                PlayList.play("SND_FSExpandWild");
                this._expandWildsDelay.start(WinAnimatorExtended.ExpandDuration);
            } else {
                this.expandWildFinished();
            }
        }
    }

    public hideExpandWilds(spin: boolean): void {
        for (let i = 0; i < this._expandWilds.length; i++) {
            let wild = this._expandWilds[i];
            this._expandWildsRotation.addChild(wild);

            if (spin) {
                Tweens.removeTweens(wild);
                Tweens.customTween(wild, {
                    tween: "position.y",
                    tweenValues: [
                        wild.position.y,
                        this.position.y - 180,
                        this.position.y,
                        this.position.y + 3.5 * this.symbolHeight,
                    ],
                    tweenDuration: [10, 10, 10],
                    tweenDelay: [3 * i],
                });
            } else {
                this.stopTimer();
                wild.goToCase(Symbol.CaseEmpty);
            }
        }
    }

    public expandWildFinished(): void {
        //hide symbols under expandWilds
        for (let i = 0; i < this._expandWilds.length; i++) {
            if (this._expandWilds[i].getCurrentCase() !== Symbol.CaseEmpty) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    this.symbols.returnSymbol(this.currentSymbols[i][j]);
                    this.currentSymbols[i][j] = null;
                }
            }
        }

        this.emit(WinAnimatorExtended.ExpandWildAnimFinish);
    }

    public hahaSoundFinished(): void {
        this._canPlay = true;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/